/**
 * Page /service-technique/planning — Planning manager hebdomadaire.
 *
 * Layout 3 colonnes : sidebar agents 88px / grille planning / sidebar fiches 180px
 *   - Couleurs par agent (pastille + bordure gauche des créneaux)
 *   - Jour courant mis en surbrillance
 *   - Drag-n-drop @dnd-kit pour déplacer/créer des créneaux
 *   - Sidebar "À affecter" avec signalements non planifiés + bloc suggestion auto
 *   - Bottom bar : compteurs + Export réunion + Nouvelle fiche
 *
 * Refonte R6.2 (mai 2026) :
 *   - Topbar glass cohérent + sub-nav avec tabs en pills glass
 *   - Sidebars en Card glass
 *   - Grille planning en Card glass avec cellules surface-hover
 *   - Drop cells avec ring tech au drop
 *   - Modal en Card glass plein écran avec backdrop blur
 *
 * Logique métier 100% préservée :
 *   - DndContext + sensors + onDragStart/onDragEnd
 *   - api.serviceTechnique.planning.getWeek/updateCreneau/createCreneau/deleteCreneau/setAgentCouleur
 *   - PALETTE 8 couleurs agents (hex hard-coded, données métier)
 *   - HOURS 8h-18h, FRENCH_DAY_NAMES Lun-Ven (5 jours)
 *   - isReadOnly = agent (lecture seule)
 *   - WeekStart aligné lundi, shiftWeek delta jours, weekLabel
 *   - AgentSidebar avec color picker
 *   - PlanningGrid + DropCell + DraggableCreneau
 *   - AffectablesSidebar avec AffectableCard draggable + suggestions
 *   - CreneauModal create/edit avec datetime-local + parsing
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import { api, ApiException } from '@/lib/api';
import { LieuSelect } from '@/components/LieuSelect';
import { AssignEquipeModal } from '@/pages/service-technique/AssignEquipeModal';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';                                 // R13 — remplace window.confirm
import { Button, Alert, Field, Input, Card } from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { ServiceTechniqueNav } from '@/components/ServiceTechniqueNav';
import {
  ChevronLeft, ChevronRight, Inbox, Plus, FileDown,
  X, AlertTriangle, CircleAlert, Circle,
  Layers as Stack, Trash2, ArrowLeft, AlertCircle, CalendarRange, Send, Users,
} from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { BoutonCloturePeriode } from '@/components/planning/BoutonCloturePeriode';
import { PlanningGrilleUnifiee } from '@/components/planning/PlanningGrilleUnifiee';
import { PlanningVueEquipe } from '@/components/planning/PlanningVueEquipe';
import { DeclarerAbsenceModal } from '@/pages/service-technique/DeclarerAbsenceModal';
import { useToast } from '@/components/ui/Toast';

// Palette agents (couleurs métier hex hard-coded, indépendantes du DA)
const PALETTE: Array<{ value: string; label: string }> = [
  { value: '#1D9E75', label: 'Vert' },
  { value: '#D4537E', label: 'Rose' },
  { value: '#BA7517', label: 'Orange' },
  { value: '#7F77DD', label: 'Violet' },
  { value: '#378ADD', label: 'Bleu' },
  { value: '#639922', label: 'Olive' },
  { value: '#D85A30', label: 'Corail' },
  { value: '#534AB7', label: 'Indigo' },
];

const FRENCH_DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];

// Types
type Agent = { id: string; fullName: string; email: string; role: string; couleurMarker: string | null };
type Creneau = {
  id: string;
  agentUserId: string;
  agentFullName: string;
  agentCouleurMarker: string | null;
  dateDebut: string;
  dateFin: string;
  titre: string;
  description: string | null;
  signalementId: string | null;
  signalementNumero: string | null;
  type: string;
  couleurMarker: string | null;
};
type Affectable = {
  id: string;
  numero: string;
  sujet: string;
  categorie: string;
  urgence: string;
  statut: string;
  localisationAdresse: string | null;
  localisationQuartier: string | null;
  isAnonymous: boolean;
  nbCreneaux?: number;
};
type Suggestion = { id: string; libelle: string; signalementIds: string[]; raison: string };

// Topbar background partagé

// =============================================================================
// PAGE
// =============================================================================

export function PlanningManagerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm, dialogElement } = useConfirmDialog();   // R13
  const { toast } = useToast();
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
  const [porteeExport, setPorteeExport] = useState<'semaine' | 'mois'>('semaine');

  // Planning UNIFIÉ (20/07) : sous-vue affichée dans la page. « Travail » = la
  // grille glissable (postes/lieux/signalements) ; « Présence » = le composant
  // transverse (semaine/personne/année + compteurs + déclaration d'absence).
  const [absModal, setAbsModal] = useState<{ agent: { id: string; fullName: string }; jourISO: string } | null>(null);

  // État semaine — aligne sur lundi
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const today = new Date();
    const day = today.getDay() === 0 ? 7 : today.getDay();
    const monday = new Date(today);
    monday.setDate(monday.getDate() - (day - 1));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [creneaux, setCreneaux] = useState<Creneau[]>([]);
  const [affectables, setAffectables] = useState<Affectable[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  // Refonte planning 21/07 : vue Équipe (défaut) / Individuelle (la grille 3 colonnes actuelle)
  const [vue, setVue] = useState<'equipe' | 'individuelle'>('equipe');
  const [agentIndividuel, setAgentIndividuel] = useState<string>('');
  useEffect(() => {
    if (!agentIndividuel && agents.length > 0) setAgentIndividuel(agents[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents]);

  // Modal édition / création
  const [modalCreneau, setModalCreneau] = useState<Creneau | null>(null);
  const [modalNew, setModalNew] = useState<{ agentId?: string; date?: Date; signalementId?: string } | null>(null);
  // Bloc 2 — modale d'affectation multi-personnes/équipe (au drop d'un signalement)
  const [modalAssign, setModalAssign] = useState<{ signalement: Affectable | null; date: Date; hour: number; template?: import('@roronoa-collectivites/shared-types').PlanningTemplate } | null>(null);

  // Drag state pour overlay visuel
  const [activeDrag, setActiveDrag] = useState<{ type: 'creneau' | 'signalement'; payload: Creneau | Affectable } | null>(null);

  // Profils & génération + jours non travaillés (vacances/fériés)
  const [profilsOpen, setProfilsOpen] = useState(false);
  const [nonTravailles, setNonTravailles] = useState<Map<string, string>>(new Map());
  // Bloc 2 — modèles de travaux types (glissables sur le planning)
  const [templates, setTemplates] = useState<import('@roronoa-collectivites/shared-types').PlanningTemplate[]>([]);

  const isReadOnly = user?.role === 'agent';

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      // R13.4 — Fix timezone : envoie YYYY-MM-DD LOCAL au lieu de toISOString() (UTC)
      // Sinon pour un lundi 18 mai 00:00 Paris, toISOString().slice(0,10) renvoyait "2026-05-17"
      // (dimanche UTC), et le backend reculait au lundi précédent = 11 mai → décalage d'1 semaine
      const y = weekStart.getFullYear();
      const m = String(weekStart.getMonth() + 1).padStart(2, '0');
      const d = String(weekStart.getDate()).padStart(2, '0');
      const localISODate = `${y}-${m}-${d}`;
      const res = await api.serviceTechnique.planning.getWeek(localISODate);
      setAgents(res.agents);
      // Charge les modèles de travaux (non bloquant)
      api.serviceTechnique.planning.listTemplates().then(setTemplates).catch(() => setTemplates([]));
      // Planning UNIFIÉ : une seule grille montre TOUT — présence (créneaux de
      // profil) ET travail (signalements / créneaux manuels). Un créneau de
      // présence dit « l'agent est là » ; un créneau de travail dit « là, et il
      // fait ça ». On ne cloisonne plus : c'est un seul planning.
      setCreneaux(res.creneaux);
      setAffectables(res.signalementsAffectables);
      setSuggestions(res.suggestionsRegroupement);
      // Jours non travaillés de la semaine (vacances/fériés) pour le grisage
      const to = new Date(weekStart); to.setDate(to.getDate() + 6);
      const toISO = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
      api.serviceTechnique.planning.joursNonTravailles(localISODate, toISO)
        .then((jnt) => setNonTravailles(new Map(jnt.map((j) => [j.date, j.libelle]))))
        .catch(() => setNonTravailles(new Map()));
      // R13.1 — log debug pour comprendre pourquoi les créneaux ne s'affichent pas dans la grille
      // À retirer une fois le bug planning corrigé
      console.log('[Planning] weekStart local envoyé:', localISODate, '| weekStart Date:', weekStart.toString(), 'fetched', res.creneaux.length, 'créneau(x):');
      res.creneaux.forEach((c, i) => {
        const debut = new Date(c.dateDebut);
        console.log(
          `  #${i + 1} id=${c.id} agentId=${c.agentUserId} dateDebut="${c.dateDebut}" → local=${debut.toString()} → ${debut.getFullYear()}/${debut.getMonth() + 1}/${debut.getDate()} ${debut.getHours()}h`
        );
      });
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement du planning');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  // Transmet la semaine courante à la paie (jalon RH)
  async function transmettrePaie() {
    const y = weekStart.getFullYear();
    const m = String(weekStart.getMonth() + 1).padStart(2, '0');
    const d = String(weekStart.getDate()).padStart(2, '0');
    const from = `${y}-${m}-${d}`;
    const fin = new Date(weekStart); fin.setDate(fin.getDate() + 7);
    const toISO = `${fin.getFullYear()}-${String(fin.getMonth() + 1).padStart(2, '0')}-${String(fin.getDate()).padStart(2, '0')}`;
    const ok = await confirm({
      title: 'Transmettre cette semaine à la paie ?',
      description: 'Les créneaux de la semaine seront marqués comme transmis. Cette action prépare la consolidation RH.',
      confirmLabel: 'Transmettre',
    });
    if (!ok) return;
    try {
      const r = await api.serviceTechnique.planning.transmettrePaie(from, toISO);
      setError(null);
      await reload();
      void r;
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Erreur lors de la transmission à la paie');
    }
  }

  // Helpers semaine
  function shiftWeek(deltaDays: number) {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + deltaDays);
    setWeekStart(next);
  }

  /** weekStart → 'YYYY-MM-DD' LOCAL (même convention que le fetch, fix R13.4). */
  function weekStartISO(): string {
    const y = weekStart.getFullYear();
    const m = String(weekStart.getMonth() + 1).padStart(2, '0');
    const d = String(weekStart.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  async function doExport(kind: 'excel' | 'pdf') {
    setExporting(kind); setError(null);
    try {
      const date = weekStartISO();
      // L'export doit refléter L'ÉCRAN : en vue individuelle, on n'exporte que
      // l'agent sélectionné ; en vue équipe, tout le monde. La portée
      // (semaine/mois) suit déjà le sélecteur.
      const cible = vue === 'individuelle' && agentIndividuel ? agentIndividuel : undefined;
      if (kind === 'excel') await api.serviceTechnique.planning.exportExcel(date, porteeExport, cible);
      else await api.serviceTechnique.planning.exportPdf(date, porteeExport, cible);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Export impossible');
    } finally { setExporting(null); }
  }

  const weekLabel = useMemo(() => {
    const start = weekStart;
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 4);
    const fmt = (d: Date) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
    return `Semaine du ${fmt(start)} au ${fmt(end)} ${end.getFullYear()}`;
  }, [weekStart]);

  const days = useMemo(() => {
    return FRENCH_DAY_NAMES.map((label, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return { label, date: d };
    });
  }, [weekStart]);

  const todayKey = useMemo(() => new Date().toDateString(), []);

  function colorOfCreneau(c: Creneau): string {
    return c.couleurMarker ?? c.agentCouleurMarker ?? '#888888';
  }

  // =============================================================================
  // DRAG-N-DROP @dnd-kit
  // =============================================================================

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  function onDragStart(event: DragStartEvent) {
    const data = event.active.data.current as
      | { type: 'creneau'; payload: Creneau }
      | { type: 'signalement'; payload: Affectable }
      | undefined;
    if (data) setActiveDrag(data);
  }

  async function onDragEnd(event: DragEndEvent) {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;

    const overData = over.data.current as { dayIdx: number; hour: number } | undefined;
    if (!overData) return;

    const targetDate = new Date(weekStart);
    targetDate.setDate(targetDate.getDate() + overData.dayIdx);
    targetDate.setHours(overData.hour, 0, 0, 0);

    const activeData = active.data.current as
      | { type: 'creneau'; payload: Creneau }
      | { type: 'signalement'; payload: Affectable }
      | { type: 'template'; payload: import('@roronoa-collectivites/shared-types').PlanningTemplate };

    try {
      if (activeData.type === 'creneau') {
        // Déplacement d'un créneau existant : on garde la durée d'origine
        const c = activeData.payload;
        const duration = new Date(c.dateFin).getTime() - new Date(c.dateDebut).getTime();
        const newFin = new Date(targetDate.getTime() + duration);
        await api.serviceTechnique.planning.updateCreneau(c.id, {
          dateDebut: targetDate.toISOString(),
          dateFin: newFin.toISOString(),
        });
      } else if (activeData.type === 'template') {
        // Bloc 2 — drop d'un modèle de travaux : ouvre la modale d'affectation
        // pré-remplie (titre + durée + nb de personnes suggéré).
        const tpl = activeData.payload;
        setModalAssign({
          signalement: null,
          date: targetDate,
          hour: overData.hour,
          template: tpl,
        });
      } else {
        // Bloc 2 — drop d'un signalement → modale d'affectation multi-personnes
        // (une, plusieurs, ou une équipe rapide ; seuls les présents proposés).
        const sig = activeData.payload;
        setModalAssign({ signalement: sig, date: targetDate, hour: overData.hour });
      }
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur lors du déplacement');
    }
  }

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="min-h-screen text-ink">
        {/* ===== Topbar glass ===== */}
        <AppHeader module="Service technique" accent="tech" />

        {/* ===== Sub-nav : onglets (ligne 1) + navigation semaine (ligne 2) =====
            Onglets délégués au nav partagé ServiceTechniqueNav (flex-wrap, menu
            déroulant Planning). La navigation semaine passe sur une 2e ligne, en
            bas à gauche, pour ne plus être poussée hors cadre à droite en mobile. */}
        <nav
          aria-label="Navigation Service technique"
          className="border-b border-border backdrop-blur-glass px-4 py-2"
          style={{ backgroundImage: 'linear-gradient(180deg, oklch(var(--c-bg-2) / 0.3), oklch(var(--c-bg) / 0.4))' }}
        >
          <div className="mx-auto max-w-[1400px] space-y-2">
            {/* Ligne 1 : onglets + actions gestionnaire (passent à la ligne si étroit) */}
            <ServiceTechniqueNav
              active="planning"
              right={
                !isReadOnly ? (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => navigate('/service-technique/equipes')}>
                      <Users className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Équipes</span>
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setProfilsOpen(true)}>
                      <CalendarRange className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Profils &amp; génération</span>
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => void transmettrePaie()}>
                      <Send className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Transmettre à la paie</span>
                    </Button>
                    <BoutonCloturePeriode service="service_technique" />
                  </>
                ) : undefined
              }
            />

            {/* Ligne 2 : navigation semaine + export */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => shiftWeek(-7)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-r-lg border border-border bg-surface/60 text-ink-muted backdrop-blur-glass transition-all hover:bg-surface/80 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                aria-label="Semaine précédente"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <span className="text-xs font-medium text-ink tabular-nums px-1">{weekLabel}</span>
              <button
                type="button"
                onClick={() => shiftWeek(7)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-r-lg border border-border bg-surface/60 text-ink-muted backdrop-blur-glass transition-all hover:bg-surface/80 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                aria-label="Semaine suivante"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>

              {/* Export planning (Lot C 6.6) : portée réglable + Excel/PDF */}
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-r-lg border border-border bg-surface/60 p-0.5 backdrop-blur-glass" role="group" aria-label="Portée de l'export">
                  <button
                    type="button"
                    onClick={() => setPorteeExport('semaine')}
                    className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${porteeExport === 'semaine' ? 'bg-tech text-white' : 'text-ink-muted hover:text-ink'}`}
                    aria-pressed={porteeExport === 'semaine'}
                  >
                    Semaine
                  </button>
                  <button
                    type="button"
                    onClick={() => setPorteeExport('mois')}
                    className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${porteeExport === 'mois' ? 'bg-tech text-white' : 'text-ink-muted hover:text-ink'}`}
                    aria-pressed={porteeExport === 'mois'}
                  >
                    Mois
                  </button>
                </div>
                <Button variant="secondary" size="sm" onClick={() => void doExport('excel')} disabled={exporting !== null}>
                  <FileDown className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">{exporting === 'excel' ? 'Export…' : 'Excel'}</span>
                </Button>
                <Button variant="secondary" size="sm" onClick={() => void doExport('pdf')} disabled={exporting !== null}>
                  <FileDown className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">{exporting === 'pdf' ? 'Export…' : 'PDF'}</span>
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* ===== Main : grille unique (présence + travail ensemble) ===== */}
        <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-4 animate-fade-in">
          {error && (
            <Alert variant="error" icon={AlertCircle} className="mb-3">
              {error}
            </Alert>
          )}

          {/* Sélecteur Équipe / Individuelle (service bloqué = service technique) */}
          {!loading && (
            <div className="mb-3 inline-flex rounded-r-lg border border-border-hi bg-surface/60 p-0.5">
              <button type="button" onClick={() => setVue('equipe')} className={`rounded-r-md px-3 py-1.5 text-sm ${vue === 'equipe' ? 'bg-tech text-white' : 'text-ink-muted'}`}>Équipe</button>
              <button type="button" onClick={() => setVue('individuelle')} className={`rounded-r-md px-3 py-1.5 text-sm ${vue === 'individuelle' ? 'bg-tech text-white' : 'text-ink-muted'}`}>Individuelle</button>
            </div>
          )}

          {loading ? (
            <div className="flex h-[60vh] items-center justify-center">
              <Card variant="glass" padded>
                <div className="flex items-center gap-3">
                  <div
                    className="h-6 w-6 animate-spin rounded-full border-2 border-tech border-t-transparent"
                    role="status"
                    aria-live="polite"
                  />
                  <span className="text-sm text-ink-muted">Chargement du planning…</span>
                </div>
              </Card>
            </div>
          ) : vue === 'equipe' ? (
            /* VUE ÉQUIPE (défaut) — 1 ligne/agent, cadres colorés alignés */
            <PlanningVueEquipe
              days={days}
              agents={agents.map((a) => ({ id: a.id, fullName: a.fullName, couleurMarker: a.couleurMarker }))}
              creneaux={creneaux}
              colorOfCreneau={colorOfCreneau}
              onSetAgentColor={isReadOnly ? undefined : async (agentId, color) => {
                try { await api.serviceTechnique.planning.setAgentCouleur(agentId, color); await reload(); }
                catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur attribution couleur'); }
              }}
              /* LOT 2 — le modal d'absence existait déjà (multi-jours + tâches en
                 conflit) mais n'était JAMAIS ouvert depuis cette grille : le state
                 absModal était du code mort. On branche les deux déclencheurs. */
              onDeclarerAbsence={isReadOnly ? undefined : (agent, jourISO) =>
                setAbsModal({ agent: { id: agent.id, fullName: agent.fullName }, jourISO })}
              onCreneauClick={(c) => !isReadOnly && setModalCreneau(c as Creneau)}
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-[88px_1fr_180px]">
              {/* Sidebar agents */}
              <AgentSidebar
                agents={agents}
                readOnly={isReadOnly}
                onSetCouleur={async (agentId, color) => {
                  try {
                    await api.serviceTechnique.planning.setAgentCouleur(agentId, color);
                    await reload();
                  } catch (err) {
                    setError(err instanceof ApiException ? err.payload.error : 'Erreur attribution couleur');
                  }
                }}
              />

              {/* Grid planning — filtré sur l'agent choisi (vue individuelle) */}
              <div>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <label className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Agent :</label>
                  <select value={agentIndividuel} onChange={(e) => setAgentIndividuel(e.target.value)} className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.fullName}</option>)}
                  </select>
                </div>
                <PlanningGrilleUnifiee
                  accent="tech"
                  afficherNumero
                  days={days}
                  todayKey={todayKey}
                  creneaux={creneaux}
                  filterAgentId={agentIndividuel}
                  onCreneauClick={(c) => !isReadOnly && setModalCreneau(c as Creneau)}
                  colorOfCreneau={colorOfCreneau}
                  nonTravailles={nonTravailles}
                />
              </div>

              {/* Sidebar fiches à affecter */}
              <AffectablesSidebar
                affectables={affectables}
                suggestions={suggestions}
                templates={templates}
                readOnly={isReadOnly}
                onClickSignalement={(s) => navigate(`/service-technique/signalements/${s.id}`)}
              />
            </div>
          )}
        </main>

        {/* ===== Bottom bar sticky glass ===== */}
        {/* Item 1.4 (retour mairie) — « le planning passe SOUS les icônes ».
            `sticky` crée un contexte d'empilement, mais SANS z-index la barre
            reste au niveau du flux : les cartes du planning (qui portent leurs
            propres z-10/z-20 pour les tooltips) passaient par-dessus.
            z-30 : au-dessus du contenu et des tooltips (z-20), en dessous des
            menus flottants (z-[100]) et des modales (z-50). */}
        <footer
          className="sticky bottom-0 z-30 mt-4 border-t border-border backdrop-blur-glass px-4 py-2.5"
          style={{ backgroundImage: 'linear-gradient(0deg, oklch(var(--c-bg-2) / 0.6), oklch(var(--c-bg) / 0.4))' }}
        >
          <div className="mx-auto flex max-w-[1400px] items-center gap-3 text-xs">
            <div className="text-ink-muted">
              {agents.length} agent{agents.length > 1 ? 's' : ''} · {creneaux.length} créneau{creneaux.length > 1 ? 'x' : ''} cette semaine
            </div>
            <div className="ml-auto flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => window.print()}
                title="Imprimer / exporter le planning pour la réunion"
              >
                <FileDown className="h-4 w-4" aria-hidden="true" /> Export réunion
              </Button>
            </div>
          </div>
        </footer>

        {/* ===== Modale de déclaration d'absence (vue Présence) ===== */}
        {absModal && (
          <DeclarerAbsenceModal
            agent={absModal.agent}
            jourISO={absModal.jourISO}
            service="service_technique"
            onClose={() => setAbsModal(null)}
            onDone={(conflits) => {
              setAbsModal(null);
              void reload();
              toast.success(conflits > 0
                ? `Absence enregistrée. ${conflits} tâche(s) à réaffecter — responsable alerté.`
                : 'Absence enregistrée.');
            }}
          />
        )}

        {/* ===== Modals création / édition ===== */}
        {modalCreneau && (
          <CreneauModal
            mode="edit"
            initial={modalCreneau}
            agents={agents}
            onClose={() => setModalCreneau(null)}
            onSubmit={async (input) => {
              await api.serviceTechnique.planning.updateCreneau(modalCreneau.id, input);
              setModalCreneau(null);
              await reload();
            }}
            onDelete={async () => {
              // R13 — Dialog R2 au lieu de window.confirm
              const ok = await confirm({
                title: 'Supprimer ce créneau ?',
                description: 'Le créneau sera retiré du planning. Action irréversible.',
                confirmLabel: 'Supprimer',
                danger: true,
              });
              if (!ok) return;
              await api.serviceTechnique.planning.deleteCreneau(modalCreneau.id);
              setModalCreneau(null);
              await reload();
            }}
          />
        )}

        {modalNew && (
          <CreneauModal
            mode="create"
            initial={{
              agentUserId: modalNew.agentId ?? agents[0]?.id ?? '',
              dateDebut: modalNew.date?.toISOString() ?? new Date(weekStart.getTime() + 8 * 3600 * 1000).toISOString(),
              dateFin: modalNew.date
                ? new Date(modalNew.date.getTime() + 2 * 3600 * 1000).toISOString()
                : new Date(weekStart.getTime() + 10 * 3600 * 1000).toISOString(),
              signalementId: modalNew.signalementId ?? null,
              titre: modalNew.signalementId
                ? affectables.find((a) => a.id === modalNew.signalementId)?.sujet ?? ''
                : '',
              type: 'autre',
            }}
            agents={agents}
            onClose={() => setModalNew(null)}
            onSubmit={async (input) => {
              const cree = await api.serviceTechnique.planning.createCreneau({
                agentUserId: input.agentUserId!,
                dateDebut: input.dateDebut!,
                dateFin: input.dateFin!,
                titre: input.titre!,
                description: input.description ?? undefined,
                signalementId: input.signalementId ?? undefined,
                type: input.type,
              });
              setModalNew(null);
              await reload();
              if (cree.nbChevauchements && cree.nbChevauchements > 0) {
                setError(`⚠ Attention : cette personne a déjà ${cree.nbChevauchements} autre(s) créneau(x) sur ce créneau horaire. Les tâches sont affichées côte à côte.`);
              }
            }}
          />
        )}

        {modalAssign && (
          <AssignEquipeModal
            signalement={modalAssign.signalement ? { id: modalAssign.signalement.id, numero: modalAssign.signalement.numero, sujet: modalAssign.signalement.sujet } : null}
            template={modalAssign.template ?? null}
            date={modalAssign.date}
            defaultHour={modalAssign.hour}
            onClose={() => setModalAssign(null)}
            onDone={() => { setModalAssign(null); void reload(); }}
          />
        )}

        {/* DragOverlay : ghost de l'élément en cours de drag */}
        <DragOverlay>
          {activeDrag?.type === 'creneau' && (
            <div
              className="rounded-r-md border-l-4 px-2 py-1 text-xs shadow-pop backdrop-blur-glass"
              style={{
                borderLeftColor: colorOfCreneau(activeDrag.payload as Creneau),
                backgroundColor: colorOfCreneau(activeDrag.payload as Creneau) + '33',
              }}
            >
              {(activeDrag.payload as Creneau).titre}
            </div>
          )}
          {activeDrag?.type === 'signalement' && (
            <div className="rounded-r-md border-l-4 border-urgent bg-surface/90 backdrop-blur-glass px-2 py-1 text-xs shadow-pop text-ink">
              {(activeDrag.payload as Affectable).sujet}
            </div>
          )}
        </DragOverlay>
        {dialogElement}{/* R13 — Dialog R2 (confirmation suppression créneau) */}
        <ProfilsDialogST
          open={profilsOpen}
          onClose={() => setProfilsOpen(false)}
          agents={agents}
          onGenerated={() => { setProfilsOpen(false); void reload(); }}
        />
      </div>
    </DndContext>
  );
}

// =============================================================================
// SIDEBAR AGENTS — palette de couleurs cliquable
// =============================================================================

function AgentSidebar({
  agents,
  readOnly,
  onSetCouleur,
}: {
  agents: Agent[];
  readOnly: boolean;
  onSetCouleur: (agentId: string, color: string | null) => void;
}) {
  const [pickerOpenFor, setPickerOpenFor] = useState<string | null>(null);

  return (
    <aside
      className="rounded-r-xl border border-border bg-surface/55 backdrop-blur-glass p-2 shadow-soft"
      aria-label="Équipe"
    >
      <div className="mb-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Équipe</div>
      <ul className="space-y-1">
        {agents.map((a) => {
          const color = a.couleurMarker ?? '#A8A29E';
          return (
            <li key={a.id} className="relative">
              <button
                type="button"
                disabled={readOnly}
                onClick={() => !readOnly && setPickerOpenFor(pickerOpenFor === a.id ? null : a.id)}
                className="flex w-full items-center gap-2 rounded-r-md px-1.5 py-1.5 text-left transition-colors hover:bg-surface/60 disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                title={readOnly ? a.fullName : 'Cliquer pour changer la couleur'}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/20"
                  style={{ backgroundColor: color }}
                  aria-hidden="true"
                />
                <span className="truncate text-xs font-medium text-ink">
                  {a.fullName.split(' ')[0]}
                </span>
              </button>
              {pickerOpenFor === a.id && (
                <div
                  className="absolute left-0 top-full z-[100] mt-1 rounded-r-lg border border-border bg-surface/95 backdrop-blur-glass p-2 shadow-pop"
                  role="dialog"
                  aria-label="Choisir une couleur"
                >
                  <div className="grid grid-cols-4 gap-1">
                    {PALETTE.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => {
                          onSetCouleur(a.id, c.value);
                          setPickerOpenFor(null);
                        }}
                        className="h-6 w-6 rounded-full border-2 border-transparent transition-all hover:scale-110 hover:border-tech focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                        style={{ backgroundColor: c.value }}
                        title={c.label}
                        aria-label={c.label}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      onSetCouleur(a.id, null);
                      setPickerOpenFor(null);
                    }}
                    className="mt-2 w-full text-2xs text-ink-muted transition-colors hover:text-urgent focus-visible:outline-none"
                  >
                    Effacer
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

// =============================================================================
// GRILLE PLANNING DE TRAVAIL — colonnes par jour, créneaux à hauteur = durée
// (Bloc 2 — refactor de la dette L13.10-Z1 : blocs proportionnels à la durée,
//  positionnés sur leur plage horaire réelle, comme le mockup validé.)
// =============================================================================


// =============================================================================
// SIDEBAR "À AFFECTER"
// =============================================================================

function AffectablesSidebar({
  affectables,
  suggestions,
  templates,
  readOnly,
  onClickSignalement,
}: {
  affectables: Affectable[];
  suggestions: Suggestion[];
  templates: import('@roronoa-collectivites/shared-types').PlanningTemplate[];
  readOnly: boolean;
  onClickSignalement: (s: Affectable) => void;
}) {
  return (
    <aside
      className="rounded-r-xl border border-border bg-surface/55 backdrop-blur-glass p-2 shadow-soft"
      aria-label="Fiches à affecter"
    >
      <div className="mb-2 flex items-center gap-1 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
        <Inbox className="h-3 w-3" aria-hidden="true" /> À affecter ({affectables.length})
      </div>

      {affectables.length === 0 && (
        <p className="text-2xs text-ink-dim">Aucun signalement en attente</p>
      )}

      <ul className="space-y-1.5">
        {affectables.slice(0, 12).map((s) => (
          <AffectableCard
            key={s.id}
            signalement={s}
            readOnly={readOnly}
            onClick={() => onClickSignalement(s)}
          />
        ))}
      </ul>

      {/* Suggestions auto */}
      {suggestions.length > 0 && (
        <div className="mt-3 rounded-r-md border border-warning/40 bg-warning/10 backdrop-blur-glass p-2">
          <div className="flex items-center gap-1 text-2xs font-medium text-warning">
            <Stack className="h-3 w-3" aria-hidden="true" /> Suggestion auto
          </div>
          {suggestions.slice(0, 2).map((sg) => (
            <div key={sg.id} className="mt-1.5">
              <div className="text-2xs leading-tight text-ink">{sg.libelle}</div>
              <div className="text-[9px] text-ink-muted">{sg.raison}</div>
            </div>
          ))}
        </div>
      )}
      {/* Modèles de travaux (glissables) */}
      {templates.length > 0 && !readOnly && (
        <div className="mt-3 border-t border-border pt-2">
          <div className="mb-1.5 flex items-center gap-1 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
            <Stack className="h-3 w-3" aria-hidden="true" /> Modèles
          </div>
          <ul className="space-y-1.5">
            {templates.map((t) => (
              <TemplateCard key={t.id} template={t} />
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}

function TemplateCard({ template }: { template: import('@roronoa-collectivites/shared-types').PlanningTemplate }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `template-${template.id}`,
    data: { type: 'template', payload: template },
  });
  return (
    <li>
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        className={`cursor-grab rounded-r-md border border-tech/40 bg-tech-tint/30 backdrop-blur-glass px-2 py-1.5 text-left shadow-soft transition-opacity ${isDragging ? 'opacity-30' : 'opacity-100'}`}
      >
        <div className="text-2xs font-semibold text-ink">{template.titre}</div>
        <div className="text-[10px] text-ink-muted">{Math.round(template.dureeMin / 60 * 10) / 10}h · {template.nbPersonnes} pers.</div>
      </div>
    </li>
  );
}

function AffectableCard({
  signalement,
  readOnly,
  onClick,
}: {
  signalement: Affectable;
  readOnly: boolean;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `affectable-${signalement.id}`,
    data: { type: 'signalement', payload: signalement },
    disabled: readOnly,
  });

  const urgenceColor =
    signalement.urgence === 'urgent' ? '#E24B4A' :
    signalement.urgence === 'rapide' ? '#EF9F27' :
    '#534AB7';

  const UrgenceIcon =
    signalement.urgence === 'urgent' ? AlertTriangle :
    signalement.urgence === 'rapide' ? CircleAlert : Circle;

  return (
    <li>
      <div
        ref={setNodeRef}
        className={`rounded-r-md border border-border border-l-[3px] bg-surface/70 backdrop-blur-glass px-2 py-1.5 text-left shadow-soft transition-opacity ${
          isDragging ? 'opacity-30' : 'opacity-100'
        } ${!readOnly ? 'cursor-grab' : ''}`}
        style={{ borderLeftColor: urgenceColor }}
        {...listeners}
        {...attributes}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-sm"
        >
          <div className="flex items-center gap-1">
            <UrgenceIcon className="h-2.5 w-2.5" style={{ color: urgenceColor }} aria-hidden="true" />
            <span className="font-mono text-[9px] text-ink-muted">{signalement.numero}</span>
            {signalement.isAnonymous && <span className="text-[9px] text-ink-dim">· anon</span>}
          </div>
          <div className="line-clamp-1 text-2xs font-medium text-ink">{signalement.sujet}</div>
          {signalement.nbCreneaux != null && signalement.nbCreneaux > 0 && (
            <div className="mt-1 inline-flex items-center gap-1 rounded-r-pill border px-1.5 py-0.5 text-[9px] font-semibold"
              style={{ backgroundColor: 'color-mix(in oklab, oklch(0.72 0.18 155) 15%, transparent)', borderColor: 'color-mix(in oklab, oklch(0.72 0.18 155) 35%, transparent)', color: 'oklch(0.83 0.14 155)' }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: 'oklch(0.8 0.16 155)' }} />
              {signalement.nbCreneaux} créneau{signalement.nbCreneaux > 1 ? 'x' : ''} planifié{signalement.nbCreneaux > 1 ? 's' : ''}
            </div>
          )}
          {(signalement.localisationAdresse || signalement.localisationQuartier) && (
            <div className="line-clamp-1 text-[10px] text-ink-muted">
              {signalement.localisationAdresse ?? signalement.localisationQuartier}
            </div>
          )}
        </button>
      </div>
    </li>
  );
}

// =============================================================================
// MODAL création / édition
// =============================================================================

type ModalInitial = {
  agentUserId?: string;
  dateDebut?: string;
  dateFin?: string;
  titre?: string;
  description?: string | null;
  signalementId?: string | null;
  type?: string;
  lieuId?: string | null;
};

function CreneauModal({
  mode,
  initial,
  agents,
  onClose,
  onSubmit,
  onDelete,
}: {
  mode: 'create' | 'edit';
  initial: Creneau | ModalInitial;
  agents: Agent[];
  onClose: () => void;
  onSubmit: (input: ModalInitial) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  function toLocalInput(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    const tz = d.getTimezoneOffset();
    const localDate = new Date(d.getTime() - tz * 60000);
    return localDate.toISOString().slice(0, 16);
  }

  const [agentUserId, setAgentUserId] = useState(initial.agentUserId ?? agents[0]?.id ?? '');
  const [titre, setTitre] = useState(initial.titre ?? '');
  const [description, setDescription] = useState(initial.description ?? '');
  const [type, setType] = useState(initial.type ?? 'autre');
  const [dateDebut, setDateDebut] = useState(toLocalInput(initial.dateDebut));
  const [dateFin, setDateFin] = useState(toLocalInput(initial.dateFin));
  // Tranche 2-a : surcharge ponctuelle du lieu/poste (« 2h imprévu ailleurs »).
  const [lieuId, setLieuId] = useState<string | null>((initial as ModalInitial).lieuId ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      await onSubmit({
        agentUserId,
        titre: titre.trim(),
        description: description.trim() || null,
        type,
        dateDebut: new Date(dateDebut).toISOString(),
        dateFin: new Date(dateFin).toISOString(),
        signalementId: (initial as ModalInitial).signalementId ?? undefined,
        lieuId,
      });
    } catch (e2) {
      setErr(e2 instanceof ApiException ? e2.payload.error : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 backdrop-blur-md p-4"
        >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md">
        <Card variant="glass" padded>
          <div className="mb-3 flex items-start justify-between gap-2">
            <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
              {mode === 'create' ? 'Nouveau créneau' : 'Modifier le créneau'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-r-md p-1 text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {err && <Alert variant="error" icon={AlertCircle} className="mb-3">{err}</Alert>}

          <form onSubmit={handleSubmit} className="space-y-3">
            <Field label="Agent" htmlFor="agentUserId" required>
              <select
                id="agentUserId"
                value={agentUserId}
                onChange={(e) => setAgentUserId(e.target.value)}
                className="h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                required
              >
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.fullName} ({a.role})
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Titre" htmlFor="titre" required>
              <Input
                id="titre"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                placeholder="Ex : Tonte stade, Réfection rue du Port…"
                maxLength={200}
                required
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Début" htmlFor="dateDebut" required>
                <Input id="dateDebut" type="datetime-local" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} required />
              </Field>
              <Field label="Fin" htmlFor="dateFin" required>
                <Input id="dateFin" type="datetime-local" value={dateFin} onChange={(e) => setDateFin(e.target.value)} required />
              </Field>
            </div>

            <Field label="Type" htmlFor="type">
              <select
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
              >
                <option value="voirie">Voirie</option>
                <option value="espaces_verts">Espaces verts</option>
                <option value="eclairage">Éclairage</option>
                <option value="proprete">Propreté</option>
                <option value="assainissement">Assainissement</option>
                <option value="batiment">Bâtiment</option>
                <option value="reunion">Réunion</option>
                <option value="formation">Formation</option>
                <option value="autre">Autre</option>
              </select>
            </Field>

            <Field label="Notes" htmlFor="description">
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="Détails ou consignes pour l'agent…"
                className="w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
              />
            </Field>

            {/* Surcharge ponctuelle du lieu. Vide = hérité du profil. */}
            <Field label="Lieu (facultatif)" htmlFor="creneau-lieu">
              <LieuSelect value={lieuId} onChange={setLieuId} />
            </Field>

            <div className="flex items-center justify-between pt-2">
              {mode === 'edit' && onDelete && (
                <Button type="button" variant="danger" size="sm" onClick={() => void onDelete()}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer
                </Button>
              )}
              <div className="ml-auto flex gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={onClose}>Annuler</Button>
                <Button type="submit" variant="primary" size="sm" disabled={submitting}>
                  {submitting ? 'Enregistrement…' : mode === 'create' ? 'Créer' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// ProfilsDialogST — profils de planning réutilisables + génération sur période
// Aligné sur l'écran scolaire ; API serviceTechnique.planning, couleur "tech".
// ============================================================================
const ST_PLANNING_TYPES: { value: PlanningCreneauTypeUI; label: string }[] = [
  { value: 'voirie', label: 'Voirie' },
  { value: 'espaces_verts', label: 'Espaces verts' },
  { value: 'eclairage', label: 'Éclairage' },
  { value: 'proprete', label: 'Propreté' },
  { value: 'assainissement', label: 'Assainissement' },
  { value: 'batiment', label: 'Bâtiment' },
  { value: 'reunion', label: 'Réunion' },
  { value: 'formation', label: 'Formation' },
  { value: 'autre', label: 'Autre' },
];

type PlanningCreneauTypeUI = 'voirie' | 'espaces_verts' | 'eclairage' | 'proprete' | 'assainissement' | 'batiment' | 'reunion' | 'formation' | 'autre';

function ProfilsDialogST({ open, onClose, agents, onGenerated }: {
  open: boolean; onClose: () => void;
  agents: { id: string; fullName: string; role: string }[];
  onGenerated: () => void;
}) {
  const JOURS_SEM = [{ n: 1, l: 'Lun' }, { n: 2, l: 'Mar' }, { n: 3, l: 'Mer' }, { n: 4, l: 'Jeu' }, { n: 5, l: 'Ven' }, { n: 6, l: 'Sam' }, { n: 7, l: 'Dim' }];
  const [profils, setProfils] = useState<import('@roronoa-collectivites/shared-types').PlanningProfil[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Création / édition de profil
  const [editProfilId, setEditProfilId] = useState<string | null>(null);
  const [nom, setNom] = useState('');
  const [type, setType] = useState<PlanningCreneauTypeUI>('voirie');
  const [plages, setPlages] = useState<import('@roronoa-collectivites/shared-types').ProfilPlage[]>([]);
  const [pJour, setPJour] = useState(1);
  const [pDebut, setPDebut] = useState('08:00');
  const [pFin, setPFin] = useState('12:00');
  // Tranche 2-a : lieu réel + poste habituels du profil (nullable, non bloquant).
  const [lieuId, setLieuId] = useState<string | null>(null);

  // Génération
  const [profilId, setProfilId] = useState('');
  const [agentSel, setAgentSel] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // Bloc 1 — Service technique : les agents travaillent pendant les vacances
  // scolaires (≠ cantine/scolaire). On coche donc « inclure les vacances » PAR
  // DÉFAUT pour éviter que le manager génère un planning troué par oubli.
  const [inclureVacances, setInclureVacances] = useState(true);

  async function loadProfils() {
    try { setProfils(await api.serviceTechnique.planning.listProfils()); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  useEffect(() => { if (open) void loadProfils(); }, [open]);

  function ajouterPlage() {
    setPlages((prev) => [...prev, { jour: pJour, debut: pDebut, fin: pFin }]);
  }
  async function creerProfil() {
    if (!nom.trim() || plages.length === 0) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      if (editProfilId) {
        const r = await api.serviceTechnique.planning.updateProfil(editProfilId, { nom: nom.trim(), type, plages, lieuId });
        setMsg(`Profil modifié. ${r.crees} créneau(x) futur(s) régénéré(s)${r.supprimes ? `, ${r.supprimes} remplacé(s)` : ''}.`);
      } else {
        await api.serviceTechnique.planning.createProfil({ nom: nom.trim(), type, plages, lieuId });
      }
      setEditProfilId(null); setNom(''); setPlages([]); setLieuId(null);
      await loadProfils(); onGenerated();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }
  function editerProfil(p: import('@roronoa-collectivites/shared-types').PlanningProfil) {
    setEditProfilId(p.id); setNom(p.nom); setType(p.type as PlanningCreneauTypeUI); setPlages(p.plages);
    setLieuId(p.lieuId ?? null);
  }
  function annulerEdition() { setEditProfilId(null); setNom(''); setPlages([]); setLieuId(null); }
  async function supprimerProfil(id: string) {
    try { await api.serviceTechnique.planning.deleteProfil(id); await loadProfils(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function generer() {
    if (!profilId || agentSel.length === 0 || !from || !to) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      const r = await api.serviceTechnique.planning.generer({ profilId, agentIds: agentSel, from, to, inclureVacances });
      setMsg(`${r.creneauxCrees} créneau(x) créé(s)${r.joursIgnores > 0 ? `, ${r.joursIgnores} jour(s) non travaillé(s) ignoré(s)` : ''}.`);
      onGenerated();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de la génération'); }
    finally { setBusy(false); }
  }

  const selStyle = 'h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg';

  return (
    <Dialog open={open} onClose={onClose} title="Profils de planning & génération" size="lg"
      footer={<Button variant="ghost" onClick={onClose}>Fermer</Button>}>
      <div className="space-y-6">
        {error && <Alert variant="error">{error}</Alert>}

        {/* Création / édition d'un profil */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-ink">{editProfilId ? 'Modifier le profil' : 'Créer un profil'}</h3>
          {editProfilId && <p className="mb-2 text-xs text-ink-muted">Les créneaux futurs (non transmis à la paie) générés depuis ce profil seront régénérés avec les nouvelles plages.</p>}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nom du profil" htmlFor="pf-nom" required>
                <Input id="pf-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Tournée voirie matin" />
              </Field>
              <Field label="Type" htmlFor="pf-type">
                <select id="pf-type" value={type} onChange={(e) => setType(e.target.value as PlanningCreneauTypeUI)} className={selStyle}>
                  {ST_PLANNING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="flex items-end gap-2">
              <Field label="Jour" htmlFor="pf-jour">
                <select id="pf-jour" value={pJour} onChange={(e) => setPJour(Number(e.target.value))} className={selStyle}>
                  {JOURS_SEM.map((j) => <option key={j.n} value={j.n}>{j.l}</option>)}
                </select>
              </Field>
              <Field label="Début" htmlFor="pf-debut"><Input id="pf-debut" type="time" value={pDebut} onChange={(e) => setPDebut(e.target.value)} /></Field>
              <Field label="Fin" htmlFor="pf-fin"><Input id="pf-fin" type="time" value={pFin} onChange={(e) => setPFin(e.target.value)} /></Field>
              <Button variant="secondary" onClick={ajouterPlage}><Plus className="h-4 w-4" /> Plage</Button>
            </div>
            {plages.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {plages.map((pl, i) => (
                  <li key={i} className="inline-flex items-center gap-1 rounded-r-pill border border-border px-2.5 py-1 text-xs text-ink">
                    {JOURS_SEM.find((j) => j.n === pl.jour)?.l} {pl.debut}–{pl.fin}
                    <button type="button" onClick={() => setPlages((prev) => prev.filter((_, idx) => idx !== i))} className="text-ink-subtle hover:text-danger">×</button>
                  </li>
                ))}
              </ul>
            )}
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">Lieu habituel (facultatif)</label>
              <LieuSelect value={lieuId} onChange={setLieuId} />
              <p className="mt-1 text-xs text-ink-subtle">Reporté sur chaque créneau généré, modifiable plage par plage, jamais obligatoire.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="tech" loading={busy} onClick={creerProfil} disabled={!nom.trim() || plages.length === 0}>{editProfilId ? 'Enregistrer les modifications' : 'Enregistrer le profil'}</Button>
              {editProfilId && <Button variant="ghost" onClick={annulerEdition}>Annuler</Button>}
            </div>
          </div>
        </section>

        {/* Liste des profils */}
        {profils.length > 0 && (
          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink">Profils existants</h3>
            <ul className="space-y-1.5">
              {profils.map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded-r-md border border-border bg-surface/40 px-3 py-2">
                  <span className="text-sm text-ink">{p.nom} <span className="text-xs text-ink-subtle">· {p.plages.length} plage(s)</span></span>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => editerProfil(p)} className="text-xs text-ink-subtle hover:text-tech-fg">Modifier</button>
                    <button type="button" onClick={() => supprimerProfil(p.id)} className="text-xs text-ink-subtle hover:text-danger">Suppr.</button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Génération */}
        <section>
          <h3 className="mb-2 text-sm font-semibold text-ink">Générer sur une période</h3>
          {msg && <Alert variant="success" className="mb-2">{msg}</Alert>}
          <div className="space-y-3">
            <Field label="Profil" htmlFor="gn-profil" required>
              <select id="gn-profil" value={profilId} onChange={(e) => setProfilId(e.target.value)} className={selStyle}>
                <option value="">— Choisir —</option>
                {profils.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
            </Field>
            <div>
              <span className="mb-1.5 block text-sm font-medium text-ink">Agents</span>
              <div className="flex flex-wrap gap-1.5">
                {agents.map((a) => {
                  const on = agentSel.includes(a.id);
                  return (
                    <button key={a.id} type="button"
                      onClick={() => setAgentSel((prev) => on ? prev.filter((x) => x !== a.id) : [...prev, a.id])}
                      className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all', on ? 'bg-tech text-white' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                      {a.fullName}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Du" htmlFor="gn-from" required><Input id="gn-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
              <Field label="Au" htmlFor="gn-to" required><Input id="gn-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
            </div>
            <label className="flex items-center gap-1.5 text-sm text-ink-muted">
              <input type="checkbox" checked={inclureVacances} onChange={(e) => setInclureVacances(e.target.checked)} />
              Générer aussi pendant les vacances/fériés (astreintes, interventions)
            </label>
            <Button variant="tech" loading={busy} onClick={generer} disabled={!profilId || agentSel.length === 0 || !from || !to}>Générer le planning</Button>
          </div>
        </section>
      </div>
    </Dialog>
  );
}
